import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  Divider,
  FormControl,
  IconButton,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import GroupsIcon from '@mui/icons-material/Groups';
import type { FunnelCohort, FunnelFilterOptions, FunnelInsightFilters, UserTraitValueOption } from '../types';
import { FUNNEL_INSIGHTS_FILTER_TEST_IDS, REPORT_FILTER_PANEL_TEST_IDS } from '../testIds';
import { CohortBuilderDialog } from './CohortBuilderDialog';

/**
 * I-B2 — Amplitude-style clause builder (P-4): rows `[property][is|is not]
 * [values…]`, values within a row are OR, rows combine as AND, «+ Filter by»
 * adds a row. Edits are a DRAFT — Apply commits ONE filters object to the
 * parent (one overlay refetch). Labels display-only, raw values on the wire
 * (P-5). Context dims (Country/Device/Variant/Attribution) join the clause
 * model with the operator fixed to «is» — the server has no scalar exclusion,
 * so «is not» is honestly absent there (RC-AC-028).
 */

const CONTEXT_PROPERTIES: Array<{ key: ContextKey; label: string }> = [
  { key: 'country', label: 'Country' },
  { key: 'device', label: 'Device' },
  { key: 'variant', label: 'Variant' },
  { key: 'attributionStatus', label: 'Attribution' },
];

type ContextKey = 'country' | 'device' | 'variant' | 'attributionStatus';

export interface FilterClause {
  /** Context key (country/device/variant/attributionStatus) or a trait key. */
  property: string;
  isContext: boolean;
  operator: 'is' | 'is_not';
  values: string[];
}

/** Applied filters → editable clause rows (context first, then is-, then is-not traits). */
export function clausesFromFilters(filters: FunnelInsightFilters): FilterClause[] {
  const clauses: FilterClause[] = [];
  for (const { key } of CONTEXT_PROPERTIES) {
    const value = filters[key];
    if (value) clauses.push({ property: key, isContext: true, operator: 'is', values: [value] });
  }
  const traits = filters.userTraits
    ?? (filters.userTraitKey && filters.userTraitValue ? { [filters.userTraitKey]: [filters.userTraitValue] } : {});
  for (const [key, values] of Object.entries(traits)) {
    if ((values ?? []).length) clauses.push({ property: key, isContext: false, operator: 'is', values });
  }
  for (const [key, values] of Object.entries(filters.userTraitsExclude ?? {})) {
    if ((values ?? []).length) clauses.push({ property: key, isContext: false, operator: 'is_not', values });
  }
  return clauses;
}

/**
 * Clause rows → applied filters. Keys are assembled in a FIXED order
 * (context dims, userTraits, userTraitsExclude) so the same selection always
 * stringifies identically — the D-F1 scope key stays stable (RC-AC-025).
 * Incomplete rows (no property / no values) are dropped, never sent.
 */
export function filtersFromClauses(clauses: FilterClause[]): FunnelInsightFilters {
  const next: FunnelInsightFilters = {};
  for (const { key } of CONTEXT_PROPERTIES) {
    const clause = clauses.find(item => item.isContext && item.property === key && item.values[0]);
    if (clause) next[key] = clause.values[0];
  }
  const userTraits: Record<string, string[]> = {};
  const userTraitsExclude: Record<string, string[]> = {};
  for (const clause of clauses) {
    if (clause.isContext || !clause.property || !clause.values.length) continue;
    const target = clause.operator === 'is_not' ? userTraitsExclude : userTraits;
    target[clause.property] = Array.from(new Set([...(target[clause.property] ?? []), ...clause.values]));
  }
  if (Object.keys(userTraits).length) next.userTraits = userTraits;
  if (Object.keys(userTraitsExclude).length) next.userTraitsExclude = userTraitsExclude;
  return next;
}

/**
 * I-B1.3 — one picker row per HUMAN label: option ids are re-minted on every
 * variable re-creation and platforms store different encodings (uuid / enum
 * code / display text), so one logical answer arrives as many raw values.
 * Merge case-insensitively; selecting a row selects ALL its raw values (the
 * filter predicate matches raws, so this also makes clauses encoding-robust).
 */
export interface MergedTraitOption {
  /** Display casing of the highest-count variant. */
  label: string;
  /** Every raw stored value sharing the label, users-desc. */
  values: string[];
  /** Sum of people across the merged raw values (absent on degraded payloads). */
  users?: number;
}

export function mergeTraitOptionsByLabel(options: UserTraitValueOption[]): MergedTraitOption[] {
  const groups = new Map<string, {
    label: string;
    labelUsers: number;
    rows: Array<{ value: string; users: number }>;
    total: number;
    hasCounts: boolean;
  }>();
  for (const option of options) {
    const key = option.label.trim().toLowerCase();
    let group = groups.get(key);
    if (!group) {
      group = { label: option.label, labelUsers: -1, rows: [], total: 0, hasCounts: false };
      groups.set(key, group);
    }
    group.rows.push({ value: option.value, users: option.users ?? 0 });
    if (typeof option.users === 'number') {
      group.hasCounts = true;
      group.total += option.users;
      if (option.users > group.labelUsers) {
        group.labelUsers = option.users;
        group.label = option.label;
      }
    }
  }
  return [...groups.values()]
    .map(group => ({
      label: group.label,
      values: group.rows.sort((a, b) => b.users - a.users).map(row => row.value),
      ...(group.hasCounts ? { users: group.total } : {}),
    }))
    .sort((a, b) => (b.users ?? 0) - (a.users ?? 0) || a.label.localeCompare(b.label));
}

export interface AppliedFilterChip {
  id: string;
  label: string;
  /** The filters object with THIS clause removed (chip ✕ applies it directly). */
  next: FunnelInsightFilters;
}

/** Banner chips (P-9/RC-AC-027): one chip per applied clause, labels resolved. */
export function buildAppliedFilterChips(
  filters: FunnelInsightFilters,
  userTraitOptions?: Record<string, UserTraitValueOption[]> | null,
): AppliedFilterChip[] {
  const clauses = clausesFromFilters(filters);
  const labelFor = (key: string, raw: string): string =>
    (userTraitOptions?.[key] ?? []).find(option => option.value === raw)?.label ?? raw;
  return clauses.map((clause, index) => {
    const property = clause.isContext
      ? CONTEXT_PROPERTIES.find(item => item.key === clause.property)?.label ?? clause.property
      : clause.property;
    const operator = clause.operator === 'is_not' ? 'is not' : 'is';
    const values = clause.isContext
      ? clause.values.join(', ')
      // Merged labels: several raw values share one label — name it once.
      : Array.from(new Set(clause.values.map(value => labelFor(clause.property, value)))).join(', ');
    return {
      id: `${clause.operator}:${clause.property}`,
      label: `${property} ${operator} ${values}`,
      next: filtersFromClauses(clauses.filter((_, i) => i !== index)),
    };
  });
}

export interface ReportFilterPanelProps {
  options: FunnelFilterOptions | null;
  /** Trait key → labeled value options (I-B1). */
  userTraitOptions?: Record<string, UserTraitValueOption[]> | null;
  /** APPLIED filters — the draft re-seeds from this on every panel open. */
  value: FunnelInsightFilters;
  /** ONE commit per Apply / Clear all / cohort apply (RC-AC-022). */
  onApply: (next: FunnelInsightFilters) => void;
  /** Enables the saved-cohorts builder/picker when provided (preserved from the strip). */
  projectId?: string;
}

export const ReportFilterPanel: React.FC<ReportFilterPanelProps> = ({
  options,
  userTraitOptions,
  value,
  onApply,
  projectId,
}) => {
  const [clauses, setClauses] = useState<FilterClause[]>(() => clausesFromFilters(value));
  const [cohortDialogOpen, setCohortDialogOpen] = useState(false);

  const appliedKey = useMemo(() => JSON.stringify(filtersFromClauses(clausesFromFilters(value))), [value]);
  useEffect(() => {
    // External changes (chip ✕ on the banner, Clear, cohort) re-seed the draft.
    setClauses(clausesFromFilters(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedKey]);

  const draftKey = JSON.stringify(filtersFromClauses(clauses));
  const dirty = draftKey !== appliedKey;

  const traitKeys = useMemo(() => Array.from(new Set([
    ...(options?.availableUserTraits ?? []),
    ...Object.keys(userTraitOptions ?? {}),
  ])).sort((a, b) => a.localeCompare(b)), [options, userTraitOptions]);

  if (!options) return null;

  const contextOptionsFor = (key: string): string[] => {
    switch (key) {
      case 'country': return options.countries;
      case 'device': return options.devices;
      case 'variant': return options.variants;
      case 'attributionStatus': return options.attributionStatuses;
      default: return [];
    }
  };

  const updateClause = (index: number, patch: Partial<FilterClause>) => {
    setClauses(current => current.map((clause, i) => (i === index ? { ...clause, ...patch } : clause)));
  };

  const applyCohort = (cohort: FunnelCohort) => {
    const cohortClauses: FilterClause[] = cohort.conditions.map(condition => ({
      property: condition.traitKey,
      isContext: false,
      operator: 'is' as const,
      values: condition.values,
    }));
    const next = [...clauses.filter(clause => clause.isContext), ...cohortClauses];
    setClauses(next);
    // A cohort «Apply» is an explicit action — commit immediately (strip parity).
    onApply(filtersFromClauses(next));
    setCohortDialogOpen(false);
  };

  return (
    <Stack spacing={1} data-testid={REPORT_FILTER_PANEL_TEST_IDS.panel} sx={{ minWidth: 560, p: 0.5 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2">Filters</Typography>
        <Typography variant="caption" color="text.secondary">
          rows combine as AND · values inside a row are OR
        </Typography>
      </Stack>

      {clauses.map((clause, index) => {
        const traitOptions = userTraitOptions?.[clause.property] ?? [];
        const mergedOptions = mergeTraitOptionsByLabel(traitOptions);
        // Selected groups: any group one of the clause's raw values belongs to;
        // raws unknown to the current option list (old deep links) keep their
        // own single-value chip instead of disappearing.
        const claimed = new Set<string>();
        const selectedOptions: MergedTraitOption[] = [];
        for (const option of mergedOptions) {
          if (option.values.some(raw => clause.values.includes(raw))) {
            selectedOptions.push(option);
            option.values.forEach(raw => claimed.add(raw));
          }
        }
        for (const raw of clause.values) {
          if (!claimed.has(raw)) selectedOptions.push({ label: raw, values: [raw] });
        }
        return (
          <React.Fragment key={index}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid={REPORT_FILTER_PANEL_TEST_IDS.clauseRow(index)}
          >
            <Typography variant="caption" color="text.secondary" sx={{ width: 34, flex: 'none' }}>
              {index === 0 ? 'where' : 'and'}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                displayEmpty
                value={clause.property}
                data-testid={REPORT_FILTER_PANEL_TEST_IDS.clauseProperty(index)}
                onChange={event => {
                  const property = event.target.value;
                  const isContext = CONTEXT_PROPERTIES.some(item => item.key === property);
                  updateClause(index, { property, isContext, operator: 'is', values: [] });
                }}
                renderValue={selected => {
                  if (!selected) return <em>Property…</em>;
                  return CONTEXT_PROPERTIES.find(item => item.key === selected)?.label ?? selected;
                }}
                sx={{ fontSize: 13 }}
              >
                {traitKeys.length > 0 && <ListSubheader disableSticky>Variables</ListSubheader>}
                {traitKeys.map(key => (
                  <MenuItem key={`trait:${key}`} value={key} sx={{ fontSize: 13 }}>{key}</MenuItem>
                ))}
                <ListSubheader disableSticky>Context</ListSubheader>
                {CONTEXT_PROPERTIES.filter(item => contextOptionsFor(item.key).length > 0).map(item => (
                  <MenuItem key={`context:${item.key}`} value={item.key} sx={{ fontSize: 13 }}>{item.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 88 }}>
              <Select
                value={clause.operator}
                disabled={clause.isContext}
                data-testid={REPORT_FILTER_PANEL_TEST_IDS.clauseOperator(index)}
                onChange={event => updateClause(index, { operator: event.target.value as FilterClause['operator'] })}
                sx={{ fontSize: 13 }}
              >
                <MenuItem value="is" sx={{ fontSize: 13 }}>is</MenuItem>
                {/* Server-side negation exists only for traits — hide, don't fake (RC-AC-028). */}
                {!clause.isContext && <MenuItem value="is_not" sx={{ fontSize: 13 }}>is not</MenuItem>}
              </Select>
            </FormControl>
            {clause.isContext ? (
              <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                <Select
                  displayEmpty
                  value={clause.values[0] ?? ''}
                  data-testid={REPORT_FILTER_PANEL_TEST_IDS.clauseValues(index)}
                  onChange={event => updateClause(index, { values: event.target.value ? [event.target.value] : [] })}
                  renderValue={selected => (selected ? selected : <em>Select value…</em>)}
                  sx={{ fontSize: 13 }}
                >
                  <MenuItem value=""><em>Any</em></MenuItem>
                  {contextOptionsFor(clause.property).map(option => (
                    <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>{option}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Autocomplete
                multiple
                size="small"
                disabled={!clause.property}
                options={mergedOptions}
                value={selectedOptions}
                isOptionEqualToValue={(option, selected) =>
                  option.label.trim().toLowerCase() === selected.label.trim().toLowerCase()}
                getOptionLabel={option => option.label}
                onChange={(_event, next) =>
                  updateClause(index, { values: Array.from(new Set(next.flatMap(option => option.values))) })}
                data-testid={REPORT_FILTER_PANEL_TEST_IDS.clauseValues(index)}
                renderOption={(props, option) => (
                  <li {...props} key={option.label.toLowerCase()}>
                    <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between" sx={{ width: '100%' }}>
                      <span>{option.label}</span>
                      {typeof option.users === 'number' && (
                        <Typography variant="caption" color="text.secondary">
                          {option.users.toLocaleString('en-US')}
                        </Typography>
                      )}
                    </Stack>
                  </li>
                )}
                renderTags={(tagValue, getTagProps) => tagValue.map((option, tagIndex) => (
                  <Chip
                    {...getTagProps({ index: tagIndex })}
                    key={option.label.toLowerCase()}
                    label={option.label}
                    size="small"
                  />
                ))}
                renderInput={params => (
                  <TextField {...params} placeholder={clause.values.length ? undefined : 'Select value(s)…'} />
                )}
                sx={{ flex: 1, minWidth: 200 }}
              />
            )}
            <IconButton
              size="small"
              aria-label="remove filter clause"
              data-testid={REPORT_FILTER_PANEL_TEST_IDS.clauseRemove(index)}
              onClick={() => setClauses(current => current.filter((_, i) => i !== index))}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
          {clause.operator === 'is_not' && (
            /* RC-AC-024: the NULL asymmetry stated where the choice is made. */
            <Typography variant="caption" color="text.secondary" sx={{ pl: '42px', mt: '-4px !important' }}>
              keeps users who never set «{clause.property || 'this variable'}»
            </Typography>
          )}
          </React.Fragment>
        );
      })}

      <Button
        size="small"
        data-testid={REPORT_FILTER_PANEL_TEST_IDS.addClause}
        onClick={() => setClauses(current => [...current, { property: '', isContext: false, operator: 'is', values: [] }])}
        sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
      >
        + Filter by
      </Button>

      <Divider />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Button
            size="small"
            color="inherit"
            data-testid={REPORT_FILTER_PANEL_TEST_IDS.clearAll}
            onClick={() => {
              setClauses([]);
              onApply({});
            }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Clear all
          </Button>
          {projectId && (
            <>
              <Tooltip title="Saved cohorts">
                <IconButton
                  size="small"
                  aria-label="open cohorts"
                  data-testid={FUNNEL_INSIGHTS_FILTER_TEST_IDS.cohortsButton}
                  onClick={() => setCohortDialogOpen(true)}
                >
                  <GroupsIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <CohortBuilderDialog
                open={cohortDialogOpen}
                onClose={() => setCohortDialogOpen(false)}
                projectId={projectId}
                userTraitOptions={userTraitOptions}
                availableTraitKeys={options.availableUserTraits}
                onApplyCohort={applyCohort}
              />
            </>
          )}
        </Stack>
        <Button
          size="small"
          variant="contained"
          disabled={!dirty}
          data-testid={REPORT_FILTER_PANEL_TEST_IDS.apply}
          onClick={() => onApply(filtersFromClauses(clauses))}
          sx={{ textTransform: 'none' }}
        >
          Apply
        </Button>
      </Stack>
    </Stack>
  );
};
